// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import RoleGuard from '@/components/RoleGuard';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { ROLES } from '@/hooks/useRoles';
import { HomeRedirect } from '@/lib/home';

// Existing pages
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import GuardianConsentPage from '@/pages/GuardianConsentPage';
import DashboardPage from '@/pages/DashboardPage';
import APIKeysPage from '@/pages/APIKeysPage';
import UsagePage from '@/pages/UsagePage';
import BillingPage from '@/pages/BillingPage';
import CouponsPage from '@/pages/CouponsPage';
import ProxyPage from '@/pages/ProxyPage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsPage from '@/pages/TermsPage';
import NotFoundPage from '@/pages/NotFoundPage';
import AppShell from '@/components/shell/AppShell';
import { EnvironmentProvider } from '@/contexts/EnvironmentContext';
import ComingSoonPage from '@/pages/ComingSoonPage';
import SettingsPage from '@/pages/SettingsPage';
import NotificationsPage from '@/pages/NotificationsPage';

// New pages
import MembersPage from '@/pages/team/MembersPage';
import RolesPage from '@/pages/RolesPage';
import TeamBillingPage from '@/pages/team/TeamBillingPage';
import TeamUsagePage from '@/pages/team/TeamUsagePage';
import SupportUsersPage from '@/pages/support/SupportUsersPage';
import ServicesPage from '@/pages/admin/ServicesPage';
import AuditLogsPage from '@/pages/admin/AuditLogsPage';
import EmailSettingsPage from '@/pages/admin/EmailSettingsPage';
import PricingPage from '@/pages/admin/PricingPage';
import CreditsPage from '@/pages/admin/CreditsPage';
import ApplyPage from '@/pages/startups/ApplyPage';
import ProgrammePage from '@/pages/programme/ProgrammePage';
import StartupsAdminPage from '@/pages/admin/StartupsAdminPage';
import PlatformAnalyticsPage from '@/pages/admin/PlatformAnalyticsPage';
import TrafficDataPage from '@/pages/admin/TrafficDataPage';
import HealthPage from '@/pages/platform/HealthPage';
import AdminsPage from '@/pages/platform/AdminsPage';
import TripsPage from '@/pages/TripsPage';
import PrivacyProfilesPage from '@/pages/admin/PrivacyProfilesPage';

// Redirects authenticated users away from /login and /register
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" /></div>;
  // Signed in: superadmins land on Platform Analytics, everyone else on their dashboard.
  if (isAuthenticated) return <HomeRedirect />;
  return <>{children}</>;
};

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000 } },
});

const ADMIN_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN];
const ADMIN_SUPPORT_ROLES = [...ADMIN_ROLES, ROLES.SUPPORT_STAFF];
const TEAM_ROLES = [ROLES.ENTERPRISE_USER, ROLES.TEAM_MEMBER];
const PROGRAMME_ROLES = [ROLES.PROGRAMME_OFFICER, ...ADMIN_ROLES];
const ALL_ROLES = Object.values(ROLES);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <EnvironmentProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            {/* Public: a signed-in visitor is sent to their home page */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
            {/* Not a PublicRoute: the guardian may be signed in to their own account. */}
            <Route path="/guardian-consent" element={<GuardianConsentPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/" element={<RoleGuard roles={ALL_ROLES}><HomeRedirect /></RoleGuard>} />

            {/* Authenticated — all roles */}
            <Route element={<RoleGuard roles={ALL_ROLES}><AppShell /></RoleGuard>}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/keys" element={<APIKeysPage />} />
              <Route path="/tokens" element={<Navigate to="/keys" replace />} />
              <Route path="/usage" element={<UsagePage />} />
              <Route path="/trips" element={<TripsPage />} />
              <Route path="/startups/apply" element={<ApplyPage />} />
              <Route path="/programme" element={<RoleGuard roles={PROGRAMME_ROLES}><ProgrammePage /></RoleGuard>} />
              <Route path="/admin/startups" element={<RoleGuard roles={ADMIN_ROLES}><StartupsAdminPage /></RoleGuard>} />
              <Route path="/proxy" element={<ProxyPage />} />
              <Route path="/billing" element={
                <RoleGuard roles={[ROLES.PRIVATE_USER, ROLES.ENTERPRISE_USER]}>
                  <BillingPage />
                </RoleGuard>
              } />

              {/* /coupons was an unguarded duplicate of /admin/coupons — same
                  component, no RoleGuard, so any authenticated user could
                  create/delete coupons. Redirect to the properly-gated path
                  instead of keeping two routes to the same admin page. */}
              <Route path="/coupons" element={<Navigate to="/admin/coupons" replace />} />

              {/* Team */}
              <Route path="/team" element={<RoleGuard roles={TEAM_ROLES}><TeamUsagePage /></RoleGuard>} />
              <Route path="/team/members" element={<RoleGuard roles={[ROLES.ENTERPRISE_USER]}><MembersPage /></RoleGuard>} />
              <Route path="/team/billing" element={<RoleGuard roles={[ROLES.ENTERPRISE_USER]}><TeamBillingPage /></RoleGuard>} />
              <Route path="/team/usage" element={<RoleGuard roles={TEAM_ROLES}><TeamUsagePage /></RoleGuard>} />
              <Route path="/roles" element={<RolesPage />} />

              {/* New nav items without a page yet */}
              <Route path="/integrations" element={<ComingSoonPage title="Integrations" description="Webhooks, SDKs, and migration helpers are on the way." />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/settings" element={<SettingsPage />} />

              {/* Support */}
              <Route path="/support/users" element={<RoleGuard roles={ADMIN_SUPPORT_ROLES}><SupportUsersPage /></RoleGuard>} />

              {/* Admin */}
              <Route path="/admin/services" element={<RoleGuard roles={ADMIN_ROLES}><ServicesPage /></RoleGuard>} />
              <Route path="/admin/pricing" element={<RoleGuard roles={ADMIN_ROLES}><PricingPage /></RoleGuard>} />
              <Route path="/admin/audit-logs" element={<RoleGuard roles={ADMIN_ROLES}><AuditLogsPage /></RoleGuard>} />
              <Route path="/admin/email-settings" element={<RoleGuard roles={[ROLES.SUPER_ADMIN]}><EmailSettingsPage /></RoleGuard>} />
              <Route path="/admin/privacy" element={<RoleGuard roles={[ROLES.SUPER_ADMIN]}><PrivacyProfilesPage /></RoleGuard>} />
              <Route path="/admin/analytics" element={<RoleGuard roles={ADMIN_ROLES}><PlatformAnalyticsPage /></RoleGuard>} />
              <Route path="/admin/traffic" element={<RoleGuard roles={ADMIN_ROLES}><TrafficDataPage /></RoleGuard>} />
              <Route path="/admin/coupons" element={<RoleGuard roles={ADMIN_ROLES}><CouponsPage /></RoleGuard>} />
              <Route path="/admin/credits" element={<RoleGuard roles={ADMIN_ROLES}><CreditsPage /></RoleGuard>} />

              {/* Platform — super_admin only */}
              <Route path="/platform/health" element={<RoleGuard roles={[ROLES.SUPER_ADMIN]}><HealthPage /></RoleGuard>} />
              <Route path="/platform/admins" element={<RoleGuard roles={[ROLES.SUPER_ADMIN]}><AdminsPage /></RoleGuard>} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Router>
        </EnvironmentProvider>
        <Toaster position="top-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}
